import { CheckCircleTwoTone, DeleteOutlined, DownOutlined } from '@ant-design/icons';
import { Dropdown } from 'antd';
import { type ItemType, type MenuDividerType } from 'antd/es/menu/interface';
import { isEmpty, orderBy } from 'lodash';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { __RouterContext } from 'react-router';
import { useTheme } from 'styled-components';

import { type TDisplayColumnData } from 'components/data/types';
import { DataTableContext } from 'components/DataTable';
import {
  type TDataTableStorageKey,
  type TDropdownAction,
  type TQueryFilter,
} from 'components/DataTable/types';
import { tableStorage } from 'components/DataTable/utils';
import { Button, Modal, Tooltip } from 'components/ui';
import {
  type BladeCardsScope,
  BladeCards,
  useBladeCardViewsLazy,
} from 'horizon/components/Damages/BladeCards';
import {
  type AtlasGqlTableView,
  useTableViewLazyQuery,
  useTableViewsQuery,
} from 'types/atlas-graphql';
import { useAccountContext } from 'utils/account/AccountContext';

import { type ConditionsState, ConditionsModal } from '../Views/Conditions';

import { ColumnPicker } from './ColumnPicker';
import { columnPickerStorage, updateCustomCols } from './ColumnPicker/utils';
import { type DeleteViewState, DeleteViewModal } from './DeleteView';
import { MultiSorter } from './MultiSorter';
import { multiSorterStorage, updateSort } from './MultiSorter/utils';
import { type SaveViewState, SaveViewModal } from './SaveView';
import { type TExtensionColumn, type TMultiSortColumn } from './types';
import {
  isViewAndTableStateEqual,
  mapColumns,
  mapMultiSorts,
  removeDefaultFilters,
  updateView,
} from './utils';
import { StyledButton, StyledRow, StyledTitle } from './Views.style';

type ViewsProps = {
  tableId?: string;
  columnDefs: TDisplayColumnData[];
  storageKey?: TDataTableStorageKey;
  showSavedViews: boolean;
  showColumnPicker: boolean;
  showMultiSorter: boolean;
  bladeCardsScope?: BladeCardsScope;
  lockedFilters?: TQueryFilter[];
  savedViewCustomFields?: any;
  onSavedViewChange?: (tableView: AtlasGqlTableView) => void;
  isPrioritizing?: boolean;
  showClearSortAndFilter: boolean;
  onClearSortAndFilter?: () => void;
  onClearSelection: () => void;
  isClearSortAndFilterDisabled: boolean;
  onTableChange: (pagination: any, filters: any, sorter: any, extra?: any) => void;
  additionalItems: TDropdownAction[];
};

export const Views: React.FunctionComponent<ViewsProps> = ({
  tableId,
  columnDefs,
  storageKey,
  showSavedViews,
  showColumnPicker,
  showMultiSorter,
  bladeCardsScope,
  lockedFilters,
  savedViewCustomFields,
  onSavedViewChange,
  isPrioritizing,
  showClearSortAndFilter,
  onClearSortAndFilter,
  onClearSelection,
  isClearSortAndFilterDisabled,
  onTableChange,
  additionalItems = [],
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const routerContext = useContext(__RouterContext);
  const location = routerContext?.location;
  const history = routerContext?.history;
  const viewParamAppliedRef = useRef(false);
  const [showColumnPickerModal, setShowColumnPickerModal] = useState(false);
  const [showMultiSorterModal, setShowMultiSorterModal] = useState(false);
  const [conditionsState, setConditionsState] = useState<ConditionsState>({
    isOpen: false,
  });
  const [saveViewState, setSaveViewState] = useState<SaveViewState>({
    isOpen: false,
  });
  const [deleteViewState, setDeleteViewState] = useState<DeleteViewState>({
    isOpen: false,
  });

  const { user } = useAccountContext();

  const tableViewStorageManager = tableStorage({ storageKey: `${tableId}_VIEW`, user });
  const columnPickerStorageManager = columnPickerStorage({ storageKey: storageKey?.COLUMN_PICKER });
  const multiSorterStorageManager = multiSorterStorage({ storageKey: storageKey?.MULTI_SORT });

  const {
    pagination,
    filteredInfo: _filteredInfo,
    sortedInfo,
    updateSortedInfo,
    tableView,
    updateTableView,
    customizedColumnSet,
    updateCustomizedColumnSet,
    multiSortDef,
    updateMultiSortDef,
  } = useContext(DataTableContext);
  const filteredInfo = removeDefaultFilters(tableId, _filteredInfo);

  // checks to see if the view and the table state are different
  const isViewSaved = useMemo(() => {
    return isViewAndTableStateEqual(tableView, {
      filters: filteredInfo,
      sorts: sortedInfo,
      columns: customizedColumnSet,
      multiSorts: multiSortDef,
    });
  }, [tableView, filteredInfo, sortedInfo, customizedColumnSet, multiSortDef]);

  const isDefaultTableState =
    isClearSortAndFilterDisabled && isEmpty(customizedColumnSet) && isEmpty(multiSortDef);

  // Conditions lists everything that applies, defaults included, so it is offered whenever
  // anything does. Mirrors what `Summary` renders, so it never opens empty.
  const hasConditions =
    !!lockedFilters?.length ||
    Object.values(filteredInfo ?? {}).some(value => !isEmpty(value)) ||
    !!sortedInfo?.columnKey ||
    !isEmpty(multiSortDef) ||
    !isEmpty(customizedColumnSet);

  const { data: tableViewsData, loading: tableViewsLoading } = useTableViewsQuery({
    variables: { tableName: tableId },
    skip: !showSavedViews,
  });

  const [loadTableView, { loading: tableViewLoading }] = useTableViewLazyQuery();

  // Used to resolve a `?view=<id>` URL param against global blade-card views,
  // which are not returned by the standard `tableView(id)` query. Invoked
  // lazily from the URL-param effect below.
  const loadBladeCardViews = useBladeCardViewsLazy();

  // sets table to given view
  const applyViewToTable = (tableViewToApply?: AtlasGqlTableView) => {
    if (tableViewToApply) {
      const { filters, sorts, columns, multiSorts } = tableViewToApply;

      const sortKey = sorts?.columnKey;
      const appliedSort = sortKey ? { ...sorts, column: { sortKey } } : {};

      // set filters/sorters
      onTableChange({ ...pagination, current: 1 }, filters, appliedSort);
      // set customized columns
      updateCustomCols(columnDefs, columns, updateCustomizedColumnSet, columnPickerStorageManager);
      // set multisort
      updateSort(columnDefs, multiSorts, updateMultiSortDef, multiSorterStorageManager);

      // update view
      updateView(tableViewToApply, updateTableView, tableViewStorageManager);
      onSavedViewChange && onSavedViewChange(tableViewToApply);
    }
    onClearSelection();
  };

  // saves changes to the current view
  const handleSave = () => {
    setSaveViewState({
      isOpen: true,
      tableId,
      tableView: {
        filters: filteredInfo,
        sorts: sortedInfo,
        columns: (customizedColumnSet ?? []).map(mapColumns),
        multiSorts: (multiSortDef ?? []).map(mapMultiSorts),
        customFields: savedViewCustomFields,
      },
      columnDefs,
    });
  };

  // shows the conditions modal
  const handleConditions = () => {
    setConditionsState({
      isOpen: true,
      tableView: {
        filters: filteredInfo,
        sorts: sortedInfo,
        columns: (customizedColumnSet ?? []).map(mapColumns),
        multiSorts: (multiSortDef ?? []).map(mapMultiSorts),
      },
      columnDefs,
    });
  };

  // Load table view from localStorage and apply
  useEffect(() => {
    if (showSavedViews) {
      const tableView = tableViewStorageManager.get();
      if (tableView) {
        updateView(tableView, updateTableView, tableViewStorageManager);
      }
    }
  }, [showSavedViews]);

  // Apply a saved blade-card view from the `?view=<id>` URL param on mount, then strip it.
  // Blade card views are global and don't resolve via the standard `tableView(id)` query, so we
  // look them up via `useBladeCardViewsLazy` (typically served from the Apollo cache when
  // navigating from the dashboard).
  // Currently only used for blade cards. If needed for normal (personal) saved views in the future,
  // this could be updated to fetch the view by id if a blade card is not matched.
  useEffect(() => {
    if (viewParamAppliedRef.current || !showSavedViews || !tableId || !location || !history) {
      return;
    }
    const params = new URLSearchParams(location.search);
    const viewId = params.get('view');
    if (!viewId) {
      return;
    }

    viewParamAppliedRef.current = true;
    loadBladeCardViews().then(views => {
      const bladeCardView = views.find(v => v?.id === viewId);
      if (bladeCardView) {
        applyViewToTable({ ...bladeCardView, tableName: tableId });
      }
      params.delete('view');
      const remaining = params.toString();
      history.replace({ search: remaining ? `?${remaining}` : '' });
    });
  }, [showSavedViews, location?.search, tableId]);

  const stringifiedColumnKeys = useMemo(
    () => JSON.stringify(columnDefs.map(({ key }) => key)),
    [columnDefs]
  );

  /* Multisort ****************/
  // Load multi sort from localStorage and apply
  useEffect(
    () => {
      if (showMultiSorter) {
        const savedSort = multiSorterStorageManager.get();
        if (savedSort?.length) {
          const appliedSort = updateSort(
            columnDefs,
            savedSort,
            updateMultiSortDef,
            multiSorterStorageManager
          );
          // Only supersede the default single-column sort if a valid multi-sort
          // actually resolved (stale saved keys may drop to nothing).
          if (appliedSort.length) {
            updateSortedInfo({});
          }
        }
      }
    },
    [showMultiSorter, stringifiedColumnKeys] // TODO: see if this works on initial render only - it should
  );

  const handleCloseMultiSorterModal = ({
    sortedColumns,
  }: {
    sortedColumns?: TMultiSortColumn[];
  }) => {
    if (sortedColumns) {
      const appliedSort = updateSort(
        columnDefs,
        sortedColumns,
        updateMultiSortDef,
        multiSorterStorageManager
      );
      // An explicit multi-sort supersedes any single-column sort (including the
      // table's default sort); only clear it if a valid multi-sort resolved.
      if (appliedSort.length) {
        updateSortedInfo({});
      }
    }
    setShowMultiSorterModal(false);
  };

  /* Column Picker ************/
  // Load custom columns from localStorage and apply
  useEffect(() => {
    if (showColumnPicker) {
      const savedColumns = columnPickerStorageManager.get();
      if (savedColumns) {
        updateCustomCols(
          columnDefs,
          savedColumns,
          updateCustomizedColumnSet,
          columnPickerStorageManager,
          true
        );
      }
    }
  }, [showColumnPicker, stringifiedColumnKeys]);

  const handleCloseColumnPickerModal = ({
    orderedColumns,
  }: {
    orderedColumns?: TExtensionColumn[];
  }) => {
    if (orderedColumns) {
      updateCustomCols(
        columnDefs,
        orderedColumns,
        updateCustomizedColumnSet,
        columnPickerStorageManager
      );
    }
    setShowColumnPickerModal(false);
  };

  // menu items for 'Views' dropdown
  const menuItems: ItemType[] = [
    showColumnPicker && {
      label: t('table.customizeColumns', 'Customize Columns'),
      onClick: () => setShowColumnPickerModal(true),
      disabled: isPrioritizing,
    },
    showMultiSorter && {
      label: t('components.multipleSort', 'Multiple Sort'),
      onClick: () => setShowMultiSorterModal(true),
    },
    {
      label: t('table.conditions', 'Conditions'),
      onClick: handleConditions,
      disabled: !hasConditions,
    },
    showClearSortAndFilter && {
      label: t('table.clearFilterSort', 'Clear Filter & Sort'),
      onClick: onClearSortAndFilter,
      disabled: isClearSortAndFilterDisabled,
    },
    ...(showSavedViews
      ? [
          { type: 'divider' } as MenuDividerType,
          {
            label: t('components.saveViewAs', 'Save View As'),
            disabled: isDefaultTableState || isViewSaved,
            onClick: handleSave,
          },
          ...orderBy(
            tableViewsData?.tableViews ?? [],
            [view => view.name.toLowerCase()],
            ['asc']
          ).map(savedView => {
            const { id: savedViewId, name: savedViewName } = savedView;
            return {
              label: (
                <StyledRow>
                  <StyledTitle
                    onClick={() =>
                      loadTableView({ variables: { id: savedViewId } }).then(result => {
                        if (result.data?.tableView) {
                          applyViewToTable(result.data.tableView);
                        }
                      })
                    }
                  >
                    {savedViewId === tableView?.id && (
                      <CheckCircleTwoTone twoToneColor={theme.green} />
                    )}
                    <span title={savedViewName}>{savedViewName}</span>
                  </StyledTitle>
                  <Tooltip title={t('components.deleteView', 'Delete View')}>
                    <StyledButton
                      icon={<DeleteOutlined />}
                      onClick={() =>
                        setDeleteViewState({ isOpen: true, tableId, tableView: savedView })
                      }
                    />
                  </Tooltip>
                </StyledRow>
              ),
              disabled: false,
            };
          }),
        ]
      : []),
    ...additionalItems.map(item => {
      const { content, onClick, disabled, hoverable = true } = item;
      return {
        label: content,
        onClick,
        disabled,
        ...(hoverable ? {} : { className: 'not-hoverable' }),
      };
    }),
  ]
    .filter(Boolean)
    .map((item, index) => {
      return { ...item, key: index };
    });

  const isLoading = tableViewsLoading || tableViewLoading;

  return (
    <>
      {bladeCardsScope && (
        <BladeCards {...bladeCardsScope} tableId={tableId} applyViewToTable={applyViewToTable} />
      )}
      <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomLeft">
        <Button data-testid="datatable-views-button" loading={isLoading}>
          {t('common.views', 'Views')} <DownOutlined />
        </Button>
      </Dropdown>
      <ConditionsModal
        conditionsState={conditionsState}
        lockedFilters={lockedFilters}
        onClose={() => setConditionsState({ isOpen: false })}
      />
      {showColumnPicker && (
        <Modal
          open={showColumnPickerModal}
          closable={false}
          title={t('table.customizeColumns', 'Customize Columns')}
          onCancel={() => setShowColumnPickerModal(false)}
          destroyOnClose
          footer={null}
        >
          <ColumnPicker
            columns={columnDefs}
            customizedColumnSet={customizedColumnSet}
            onClose={handleCloseColumnPickerModal}
          />
        </Modal>
      )}
      {showMultiSorter && (
        <Modal
          open={showMultiSorterModal}
          closable={false}
          title={t('components.multipleSort', 'Multiple Sort')}
          onCancel={() => setShowMultiSorterModal(false)}
          destroyOnClose
          footer={null}
        >
          <MultiSorter
            columnDefs={columnDefs}
            multiSortDef={multiSortDef}
            onClose={handleCloseMultiSorterModal}
          />
        </Modal>
      )}
      {showSavedViews && (
        <>
          <SaveViewModal
            saveViewState={saveViewState}
            onClose={() => setSaveViewState({ isOpen: false })}
            onSuccess={savedView => {
              if (savedView) {
                applyViewToTable(savedView);
              }
            }}
          />
          <DeleteViewModal
            deleteViewState={deleteViewState}
            onClose={() => setDeleteViewState({ isOpen: false })}
            onSuccess={deletedView => {
              if (tableView?.id === deletedView?.id) {
                updateView(undefined, updateTableView, tableViewStorageManager);
              }
            }}
          />
        </>
      )}
    </>
  );
};
